import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { SectionHeader } from '../../components/ui';
import { ROUTES } from '../../routes/constants';
import { OrderbookBody } from './BrokerOrderbookPage';
import { PositionsBody } from './BrokerPositionsPage';
import { HoldingsBody } from './BrokerHoldingsPage';

const TABS = [
  { key: 'orders',    label: 'Orders',    route: ROUTES.PORTFOLIO_ORDERS },
  { key: 'positions', label: 'Positions', route: ROUTES.PORTFOLIO_POSITIONS },
  { key: 'holdings',  label: 'Holdings',  route: ROUTES.PORTFOLIO_HOLDINGS },
];

function tabFromPath(pathname) {
  if (pathname.endsWith('/positions')) return 'positions';
  if (pathname.endsWith('/holdings')) return 'holdings';
  return 'orders';
}

export default function PortfolioPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = tabFromPath(location.pathname);
  const [refreshHandler, setRefreshHandler] = useState(null);

  useEffect(() => {
    setRefreshHandler(null);
  }, [activeTab]);

  const registerRefresh = useCallback((handler) => {
    setRefreshHandler(() => (typeof handler === 'function' ? handler : null));
  }, []);

  const handleRefresh = useCallback(() => {
    refreshHandler?.();
  }, [refreshHandler]);

  return (
    <div className="max-w-7xl space-y-5">
      <SectionHeader title="Portfolio" subtitle="Orders, positions, and holdings in one place." />

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 bg-bg-hover/30 rounded-lg p-1 w-fit">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => navigate(t.route)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === t.key
                  ? 'bg-bg-elevated text-txt-primary shadow-sm'
                  : 'text-txt-secondary hover:text-txt-primary'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={!refreshHandler}
          className="btn-ghost flex items-center gap-1.5 text-xs ml-auto disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {activeTab === 'orders' && <OrderbookBody onRefreshChange={registerRefresh} />}
      {activeTab === 'positions' && <PositionsBody onRefreshChange={registerRefresh} />}
      {activeTab === 'holdings' && <HoldingsBody onRefreshChange={registerRefresh} />}
    </div>
  );
}
